import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, Wallet, LogOut, Menu, X, Shield, Store } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/?search=${encodeURIComponent(search.trim())}`);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-brand-dark/95 backdrop-blur-xl shadow-lg shadow-black/20' : 'bg-transparent'}`}
      style={{ borderBottom: scrolled ? '1px solid #1E1E2E' : 'none' }}>
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 mr-2 flex-shrink-0">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#FF6B00,#F5A623)' }}>
            <span className="text-white font-bold text-sm" style={{ fontFamily: 'Sora' }}>CM</span>
          </div>
          <span className="font-bold text-lg hidden sm:block gradient-text" style={{ fontFamily: 'Sora' }}>CryptoMart</span>
        </Link>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex-1 max-w-xl">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search products..." className="input-dark pl-9 py-2 text-sm" />
          </div>
        </form>

        {/* Desktop right */}
        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              {user.role === 'admin' && (
                <Link to="/admin" className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold"
                  style={{ background: 'rgba(255,107,0,0.1)', color: '#FF6B00' }}>
                  <Shield size={14} /> Admin
                </Link>
              )}
              {user.role === 'seller' && (
                <Link to="/seller" className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-brand-text hover:text-white transition-colors">
                  <Store size={16} /> Dashboard
                </Link>
              )}
              {(user.role === 'support' || user.role === 'admin') && (
                <Link to="/cs" className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-semibold"
                  style={{ background: 'rgba(77,162,255,0.1)', color: '#4DA2FF' }}>
                  Support
                </Link>
              )}
              <Link to="/wallet" className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-brand-text hover:text-white transition-colors">
                <Wallet size={16} /> Wallet
              </Link>
              {/* Cart icon with count */}
              <Link to="/cart" className="relative flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-brand-text hover:text-white transition-colors">
                <ShoppingCart size={16} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-xs font-bold flex items-center justify-center text-white"
                    style={{ background: '#FF6B00', fontSize: '10px' }}>
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </Link>
              <div className="relative group">
                <button className="flex items-center gap-2 px-3 py-2 rounded-lg"
                  style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
                  <User size={16} className="text-brand-orange" />
                  <span className="text-sm text-brand-text">{user.username}</span>
                </button>
                <div className="absolute right-0 top-full mt-1 w-40 rounded-xl overflow-hidden shadow-xl hidden group-hover:block"
                  style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
                  <Link to="/profile" className="block px-4 py-2 text-sm text-brand-text hover:bg-white/5">Profile</Link>
                  <Link to="/orders" className="block px-4 py-2 text-sm text-brand-text hover:bg-white/5">Orders</Link>
                  {user.role === 'seller' && <Link to="/kyb" className="block px-4 py-2 text-sm text-brand-text hover:bg-white/5">KYB Status</Link>}
                  <button onClick={logout} className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-white/5 flex items-center gap-2">
                    <LogOut size={13} /> Logout
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Cart for guests */}
              <Link to="/cart" className="relative flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-brand-text hover:text-white transition-colors">
                <ShoppingCart size={16} />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-xs font-bold flex items-center justify-center text-white"
                    style={{ background: '#FF6B00', fontSize: '10px' }}>
                    {cartCount}
                  </span>
                )}
              </Link>
              <Link to="/login" className="btn-outline py-2 px-4 text-sm">Login</Link>
              <Link to="/register" className="btn-primary py-2 px-4 text-sm">Sign Up</Link>
            </>
          )}
        </div>

        {/* Mobile: cart + menu */}
        <div className="md:hidden flex items-center gap-2 ml-auto">
          <Link to="/cart" className="relative p-2">
            <ShoppingCart size={20} className="text-brand-muted" />
            {cartCount > 0 && (
              <span className="absolute top-0 right-0 w-4 h-4 rounded-full text-xs font-bold flex items-center justify-center text-white"
                style={{ background: '#FF6B00', fontSize: '9px' }}>
                {cartCount > 9 ? '9+' : cartCount}
              </span>
            )}
          </Link>
          <button className="text-brand-muted" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden px-4 pb-4 space-y-2" style={{ borderTop: '1px solid #1E1E2E', background: '#0A0A0F' }}>
          {user ? (
            <>
              {user.role === 'seller' && <Link to="/seller" onClick={() => setMenuOpen(false)} className="block py-2 text-brand-text">Seller Dashboard</Link>}
              <Link to="/wallet" onClick={() => setMenuOpen(false)} className="block py-2 text-brand-text">Wallet</Link>
              <Link to="/orders" onClick={() => setMenuOpen(false)} className="block py-2 text-brand-text">Orders</Link>
              <Link to="/profile" onClick={() => setMenuOpen(false)} className="block py-2 text-brand-text">Profile</Link>
              {user.role === 'admin' && <Link to="/admin" onClick={() => setMenuOpen(false)} className="block py-2 text-brand-orange font-semibold">Admin Panel</Link>}
              <button onClick={logout} className="block py-2 text-red-400">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setMenuOpen(false)} className="block py-2 text-brand-text">Login</Link>
              <Link to="/register" onClick={() => setMenuOpen(false)} className="block py-2 text-brand-orange font-semibold">Sign Up Free</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
